import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Checkbox, message } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import 'react-phone-input-2/lib/style.css';
import { useReturnFormFields } from '../../../../hooks/useReturnFormFields';
import { useRegisterValues } from '../../../../providers/RegisterProvider';
import { Tooltip } from 'components/Shared/Tooltip';
import { UploadDocument } from 'components/Shared/UploadDocument';
import { Form, FormItem } from 'components/UI/Form';
import { Input } from 'components/UI/Input';
import { Option } from 'components/UI/Select';
import { useSetInitialValues } from 'hooks/account/useSetInitalValue';
import { useEventbus } from 'hooks/useEventbus';
import * as Styled from './styles';
import * as StyledShared from '../../styles';
import { fetchCompanySuccess } from 'appRedux/actions/Company';
import { useAppDispatch } from 'appRedux/hooks';
import { registerCompany } from 'appRedux/restApi';
import { AccountFormFields } from 'constants/account/account-form-fields';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import { UserTypes } from 'constants/account/user-types';
import { IS_KIOSK_AM_MODE } from 'constants/app/configs';
import { COUNTRIES } from 'constants/app/countries';
import { StepViewerEvents } from 'constants/app/eventbus-events';
import { IFile, IFormField, RestAxiosError } from 'interfaces';
import { IRegisterCompanyMediaParams } from 'interfaces/Auth';

const UploadFile = (
  name: string,
  mediaDetails: IRegisterCompanyMediaParams,
  form: FormInstance
) => {
  const { t } = useTranslation();
  const getField = (e: IFile) => {
    return e.file.originFileObj;
  };
  const { applyInitialValues } = useSetInitialValues(form);

  return (
    <Styled.UploadsWrapper>
      <FormItem
        name={name}
        label={mediaDetails.title}
        getValueFromEvent={getField}
        rules={mediaDetails?.rules}
      >
        <UploadDocument
          accept={mediaDetails.type}
          handleUploadError={() => {
            applyInitialValues(name);
          }}
          maxFileSize={30}
          title={t('generic.uploadFile')}
          multiSelect={false}
          description={mediaDetails.description}
          allowedType={mediaDetails.type}
        />
      </FormItem>
    </Styled.UploadsWrapper>
  );
};

export const RegisterCompanyStepTwo = () => {
  const { form, eventId } = useRegisterValues();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { refetch, error, isFetching, data } = useQuery<
    Awaited<ReturnType<typeof registerCompany>>,
    RestAxiosError
  >({
    queryKey: [EAccountRouteKeys.ACCOUNT_REGISTER, UserTypes.Company],
    queryFn: () => registerCompany(form.getFieldsValue(true)),
    enabled: false,
    retry: false
  });

  const { fireEvent } = useEventbus(
    {
      onEvent: ({ key, id }) => {
        if (id !== eventId) return;
        switch (key) {
          case StepViewerEvents.InitiateNextViewRender:
            form
              .validateFields()
              .then(() => {
                refetch();
              })
              .catch(() => {});
            break;
        }
      }
    },
    [form]
  );

  const handleBankAccountChange = (e: { target: { value: string } }) => {
    const inputValue = e.target.value;
    const onlyNumbers = inputValue.replace(/\D/g, '');
    const formattedValue = onlyNumbers.slice(0, 16);
    form.setFieldValue(AccountFormFields.BankAccount, formattedValue);
  };

  const returnFormFields = (field: IFormField | IFormField[]) => {
    if (Array.isArray(field)) {
      return (
        <Styled.GroupWrapper>
          {field.map(f => returnFormFields(f))}
        </Styled.GroupWrapper>
      );
    }
    switch (field.name) {
      case AccountFormFields.ContactPhone:
        return (
          <FormItem
            label={
              <>
                {field.label}
                {field.tooltip && <Tooltip>{field.tooltip}</Tooltip>}
              </>
            }
            name={field.name}
            rules={field.rules}
          >
            <StyledShared.PhoneNumberInput
              containerClass="phone-input-container"
              inputProps={{
                name: 'phone',
                autoFocus: false,
                id: 'phone'
              }}
              country="am"
            />
          </FormItem>
        );
      case AccountFormFields.BankAccount:
        return (
          <FormItem
            label={
              <>
                {field.label}
                {field.tooltip && <Tooltip>{field.tooltip}</Tooltip>}
              </>
            }
            name={field.name}
            rules={field.rules}
            isWhitespaceForbidden
          >
            <Input onChange={handleBankAccountChange} />
          </FormItem>
        );
      case AccountFormFields.CeoCountry:
        return (
          <FormItem
            label={
              <>
                {field.label}
                {field.tooltip && <Tooltip>{field.tooltip}</Tooltip>}
              </>
            }
            name={field.name}
            rules={field.rules}
          >
            <Styled.Select placeholder={t('account.placeholderCountry')}>
              {COUNTRIES.map(country => (
                <Option key={country.value} value={country.value}>
                  {country.name}
                </Option>
              ))}
            </Styled.Select>
          </FormItem>
        );
      default:
        return (
          <FormItem
            label={
              <>
                {field.label}
                {field.tooltip && <Tooltip>{field.tooltip}</Tooltip>}
              </>
            }
            name={field.name}
            rules={field.rules}
            /*
             * Exclude contact email as the whitespace condition is already
             * handled for it. This is to avoid showing two type of error for
             * contact email
             */
            isWhitespaceForbidden={
              field.name !== AccountFormFields.ContactEmail
            }
          >
            <Input />
          </FormItem>
        );
    }
  };

  const formFields = useReturnFormFields(form, [
    AccountFormFields.BankAccount,
    AccountFormFields.ContactName,
    AccountFormFields.ContactEmail,
    AccountFormFields.ContactPhone,
    AccountFormFields.RegisterGoal
  ]);

  const endingFormFields = useReturnFormFields(form, [
    AccountFormFields.CeoAddress,
    AccountFormFields.CeoCity,
    [AccountFormFields.CeoState, AccountFormFields.CeoPostCode],
    AccountFormFields.CeoCountry
  ]);

  const mediaFields: IRegisterCompanyMediaParams[] = [
    {
      title: t('account.companyMediaTitleOne'),
      type: 'application/pdf',
      description: t('generic.uploadDocumentRestrictionsDescription', {
        formats: 'pdf',
        maxFileSize: '30'
      }),
      rules: [
        {
          required: true,
          message: `${t('account.companyMediaTitleOneRequired')}`
        }
      ]
    },
    {
      title: t('account.companyMediaTitleTwo'),
      type: 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      description: t('generic.uploadDocumentRestrictionsDescription', {
        formats: 'pdf, doc',
        maxFileSize: '30'
      }),
      rules: []
    },
    {
      title: t('account.companyMediaTitleThree'),
      type: 'application/pdf',
      description: t('generic.uploadDocumentRestrictionsDescription', {
        formats: 'pdf',
        maxFileSize: '30'
      }),
      rules: []
    },
    {
      title: t('account.companyMediaTitleFour'),
      type: 'application/pdf,image/jpg,image/jpeg,image/png',
      description: t('generic.uploadDocumentRestrictionsDescription', {
        formats: 'pdf, jpg, png',
        maxFileSize: '30'
      }),
      rules: [
        {
          required: true,
          message: `${t('account.companyMediaTitleFourRequired')}`
        }
      ]
    }
  ];

  useEffect(() => {
    fireEvent({
      key: isFetching
        ? StepViewerEvents.SetNextLoading
        : StepViewerEvents.UnsetNextLoading,
      id: eventId
    });
  }, [isFetching]);

  useEffect(() => {
    if (data) {
      dispatch(fetchCompanySuccess(data.data.data));
      fireEvent({
        key: StepViewerEvents.RenderNextView,
        id: eventId
      });
    }
  }, [data, eventId]);

  useEffect(() => {
    if (error) {
      message.error(error.response?.data.message);
    }
  }, [error]);

  return (
    <StyledShared.Wrapper>
      <Form layout="vertical" form={form} name="registerCompany">
        {formFields.map(
          (field, index) =>
            field && <Fragment key={index}>{returnFormFields(field)}</Fragment>
        )}
        {mediaFields.map((singleMedia, index) => (
          <Fragment key={index}>
            {UploadFile('media[' + index + ']', singleMedia, form)}
          </Fragment>
        ))}
        <Styled.FormItemLabel>
          {t('account.companyCeoAddressSectionLabel')}
        </Styled.FormItemLabel>
        {endingFormFields.map(
          (field, index) =>
            field && <Fragment key={index}>{returnFormFields(field)}</Fragment>
        )}
        <Styled.CheckboxGroup>
          <FormItem
            name="terms"
            initialValue={false}
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(
                      t('account.requiredAgreeTermsAndConditions')
                    );
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Checkbox>
              <StyledShared.Label
                dangerouslySetInnerHTML={{
                  __html: t('account.termsAndConditions')
                }}
              />
            </Checkbox>
          </FormItem>
        </Styled.CheckboxGroup>
        <Styled.CheckboxGroup>
          <FormItem
            name="subscribed_to_news_letter"
            initialValue={false}
            valuePropName="checked"
          >
            <Checkbox>
              <StyledShared.Label
                dangerouslySetInnerHTML={{
                  __html: t('account.promotionalEmails')
                }}
              />
            </Checkbox>
          </FormItem>
        </Styled.CheckboxGroup>
        {IS_KIOSK_AM_MODE && (
          <FormItem>
            <StyledShared.AgeLimitMessage>
              {t('account.ageLimitMessage')}
            </StyledShared.AgeLimitMessage>
          </FormItem>
        )}
      </Form>
    </StyledShared.Wrapper>
  );
};
